import { ActivityInfo } from '@/app/models/activityContainerModel'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import React, { useEffect, useMemo } from 'react'
import moment from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import Timeline from '@/modules/drivers/tabs/activities/components_new/Timeline'
import { TimelineProps } from '@/modules/drivers/tabs/activities/components_new/timelineProps'

export interface WorkingDayChartProps {
  selectedDate: string | null
  plannedWorkTime: string | null
  actualWorkPlan: string | null
  dayStart: string | null
  dayEnd: string | null
  companyId: string
  driverId: number
}

const extractHour = (v: string | null): string | null => {
  if (!v) return null
  const m = v.match(/(\d{1,2}:\d{2})/)
  return m ? m[1] : null
}

const WorkingDayChart: React.FC<WorkingDayChartProps> = ({
  selectedDate,
  plannedWorkTime,
  actualWorkPlan,
  dayStart,
  dayEnd,
  companyId,
  driverId,
}) => {
  const { getDriverActivity } = useDriverActivityService()
  const { t } = useLocalize()

  const [activities, setActivities] = React.useState<ActivityInfo | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)

  const wrapperRef = React.useRef<HTMLDivElement | null>(null)
  const [chartWidth, setChartWidth] = React.useState(0)

  const startMoment = useMemo(() => {
    if (!selectedDate) return null
    const startHour =
      extractHour(dayStart) ?? extractHour(actualWorkPlan) ?? '00:00'
    return moment(`${selectedDate} ${startHour}`, 'YYYY-MM-DD HH:mm')
  }, [selectedDate, dayStart, actualWorkPlan])

  useEffect(() => {
    if (!startMoment || !companyId || !driverId) {
      setActivities(null)
      return undefined
    }

    let cancelled = false
    const fetchData = async () => {
      setActivities(null)
      setIsLoading(true)
      try {
        const end = startMoment.clone().add(24, 'hours')
        const startTime = encodeURIComponent(
          startMoment.format('YYYY-MM-DD HH:mm'),
        )
        const endTime = encodeURIComponent(end.format('YYYY-MM-DD HH:mm'))
        const data = await getDriverActivity({
          companyId,
          driverId,
          startTime,
          endTime,
          includeTrips: true,
        })
        if (!cancelled && data) {
          setActivities(data as ActivityInfo)
        }
      } catch (err) {
        console.error('Error fetching driver activity:', err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchData()
    return () => {
      cancelled = true
    }
  }, [startMoment?.valueOf(), companyId, driverId])

  React.useEffect(() => {
    if (wrapperRef.current) {
      setChartWidth(wrapperRef.current.offsetWidth)
    }
  }, [activities])

  const hasActivities =
    activities &&
    Array.isArray(activities.activities) &&
    activities.activities.length > 0

  const buildTimelineProps = (): TimelineProps => {
    const start = startMoment!.clone()
    const windowEnd = start.clone().add(1, 'days')
    const filterByWindow = (
      items: ReadonlyArray<any> | undefined,
      endKey?: string,
    ): any[] => {
      if (!items) return []
      return items.filter((item: any) => {
        const end = moment(endKey ? item[endKey] : item.endTime)
        return !(end < start || moment(item.startTime) > windowEnd)
      })
    }
    const a = activities!
    return {
      startTime: start,
      days: 1,
      width: chartWidth,
      height: 130,
      extraCurrentDate: selectedDate,
      translate: t,
      markInactiveVehicles: true,
      activities: filterByWindow(a.activities),
      places: filterByWindow(a.places),
      cardInserts: filterByWindow(a.cardInserts),
      dayActivities: filterByWindow(a.dayActivities),
      violations: filterByWindow(a.violations),
      dailyRests: filterByWindow(a.dailyRests, 'formalRestEndDate'),
      dailyPartialRegularRests: filterByWindow(
        a.dailyPartialRegularRests,
        'formalRestEndDate',
      ),
      weeklyRests: filterByWindow(a.weeklyRests),
      weeklyReducedRests: filterByWindow(a.weeklyReducedRests),
      breaks: filterByWindow(a.breaks, 'formalRestEndDate'),
      partialBreaks: filterByWindow(a.partialBreaks, 'formalRestEndDate'),
      weekRecompenses: filterByWindow(a.weekRecompenses, 'realRestEndTime'),
      outOfScopes: filterByWindow(a.outOfScopes),
      changeAreas: [],
      existingDelegations: [],
      minimalPaymentCountries: [],
      tmsData: [],
      countries: [],
      delegationMarkers: [],
      allEvents: [],
      allTripEvents: [],
      allPreviousTripEvents: [],
      periodSelections: [],
      selectedActivity: null,
      editedActivity: null,
      isAddingNew: false,
      canEdit: false,
      canGenerateAttestation: false,
      flattenChart: false,
      simpleMode: true,
      disableTooltips: false,
      showViolations: false,
      showRests: true,
      showRestParts: true,
      showDayActivities: true,
      showTrips: false,
      showDelegations: false,
      editMode: false,
      onSelected: () => {},
      onContextMenuChanged: () => {},
      onAdd: () => {},
      onEdit: () => {},
      onViolationSelected: () => {},
      onPeriodSelectionsChanged: () => {},
    } as unknown as TimelineProps
  }

  if (!selectedDate) {
    return (
      <div className="tw-mb-[20px] tw-grid tw-min-h-[130px] tw-w-full tw-place-items-center tw-bg-white tw-text-sm tw-text-content-base">
        Zaznacz dzień, aby zobaczyć wykres aktywności
      </div>
    )
  }

  return (
    <div
      ref={wrapperRef}
      className="tw-relative tw-mb-[20px] tw-min-h-[130px] tw-w-full tw-overflow-visible tw-bg-white"
    >
      {isLoading && (
        <div className="tw-absolute tw-inset-0 tw-z-[999] tw-grid tw-place-items-center tw-bg-white">
          <LoaderIcon className="tw-animate-spin" />
        </div>
      )}
      {hasActivities && startMoment && selectedDate && chartWidth > 0 && (
        <Timeline
          key={`workday-${startMoment.valueOf()}`}
          {...buildTimelineProps()}
        />
      )}
    </div>
  )
}

export default WorkingDayChart
